import { SharedClock } from './clock';
import { PeerStore } from './peers';
import {
  isChatResult,
  isConnection,
  isManifest,
  isPong,
  isSnapshot,
  isSpeechDelivery,
  isMuteResult,
  isEmoteResult,
} from './protocol';
import type { Connection, PoseCommand, Snapshot } from './protocol';
import { ChatSender } from './chatSender';
import { SpeechHistory } from './speechHistory';
import { randomId } from './randomId';
import { MuteSender } from './muteSender';
import { EmoteSender } from './emoteSender';
import { activeGesture, type Gesture } from './emotes';
import { csrfToken } from '../account/csrf';

export interface NetworkDiagnostics {
  readonly status: string;
  readonly id: string | null;
  readonly count: number;
  readonly peers: number;
  readonly tick: number;
  readonly cityEpoch: number | null;
  readonly animationSeconds: number | null;
}

export class CityClient {
  readonly clock = new SharedClock();
  readonly peers = new PeerStore();
  readonly speech = new SpeechHistory();
  readonly chat = new ChatSender(() => this.socket);
  readonly mutes = new MuteSender(() => this.socket);
  readonly gestures = new EmoteSender(() => this.socket);
  private acting: Gesture | null = null;
  private readonly client = randomId();
  private readonly abort = new AbortController();
  private socket: WebSocket | null = null;
  private id: string | null = null;
  private count = 0;
  private tick = 0;
  private generation: string | null = null;
  private muteGeneration: string | null = null;
  private status = 'Connecting';
  private retries = 0;
  private retry: ReturnType<typeof setTimeout> | null = null;
  private pulse: ReturnType<typeof setInterval> | null = null;
  private lastSnapshot = 0;
  private lastPing = 0;
  private lastPose = '';
  private sequence = 0;
  private received = false;

  constructor(
    private readonly pose: () => Omit<PoseCommand, 'type' | 'seq'>,
    private readonly connected: (clock: SharedClock, players: Snapshot['players']) => void,
    private readonly label: (text: string) => void,
    /** The server would not admit this visitor: the account decides whether they stay. */
    private readonly refused: () => void
  ) {}

  start(): void {
    this.setStatus('Connecting');
    void this.connect();
  }

  diagnostics(): NetworkDiagnostics {
    return {
      status: this.status,
      id: this.id,
      count: this.count,
      peers: this.peers.size,
      tick: this.tick,
      cityEpoch: this.received ? this.clock.cityEpoch() : null,
      animationSeconds: this.received ? this.clock.animationSeconds() : null,
    };
  }

  dispose(): void {
    this.chat.fail();
    this.mutes.reset();
    this.gestures.fail();
    this.abort.abort();
    if (this.retry !== null) clearTimeout(this.retry);
    if (this.pulse !== null) clearInterval(this.pulse);
    this.socket?.close(1000);
    this.peers.clear();
  }

  private setStatus(value: string): void {
    this.status = value;
    this.label(value);
  }

  currentGesture(): Gesture | null {
    return activeGesture(this.acting, this.clock.serverEpoch());
  }

  private async connect(): Promise<void> {
    if (this.abort.signal.aborted) return;
    try {
      const path = '/api/worlds/amsterdam1538/';
      const manifest = await this.request(path);
      if (!isManifest(manifest)) throw new Error('Invalid city manifest');
      if (manifest.asset_version !== __CITY_ASSET_VERSION__ || manifest.seed !== 1538) {
        this.setStatus('City assets changed · reload to reconnect');
        return;
      }
      const csrf = csrfToken();
      if (!csrf) throw new Error('City session could not be established');
      const response = await this.request(`${path}connection/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf },
        body: JSON.stringify({
          client_id: this.client,
          world_version: __CITY_ASSET_VERSION__,
          seed: 1538,
          pose: { ...this.pose(), type: 'pose', seq: this.sequence },
        }),
      });
      if (!isConnection(response)) throw new Error('Invalid city connection');
      if (!this.abort.signal.aborted) this.open(response);
    } catch (error) {
      if (this.abort.signal.aborted) return;
      this.setStatus(
        error instanceof Error && error.message === 'City reconnecting'
          ? 'City reconnecting'
          : 'City connection unavailable · retrying'
      );
      this.scheduleRetry();
    }
  }

  private async request(path: string, options: RequestInit = {}): Promise<unknown> {
    const response = await fetch(path, {
      ...options,
      signal: this.abort.signal,
      credentials: 'same-origin',
    });
    if (response.status === 401 || response.status === 403) this.refused();
    if (!response.ok)
      throw new Error(
        response.status === 503 ? 'City reconnecting' : 'City connection unavailable'
      );
    return response.json() as Promise<unknown>;
  }

  private open(connection: Connection): void {
    this.id = connection.id;
    this.generation = null;
    this.lastSnapshot = performance.now();
    const url = new URL(connection.websocket_path, location.href);
    url.protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(url, [connection.protocol, `ticket.${connection.ticket}`]);
    this.socket = socket;
    socket.onmessage = (event: MessageEvent<unknown>) => {
      this.message(event);
    };
    socket.onopen = () => {
      this.lastPose = '';
      this.pulse = setInterval(() => {
        this.sendPose();
      }, 100);
    };
    socket.onclose = () => {
      this.chat.fail();
      this.mutes.fail();
      this.gestures.fail();
      this.acting = null;
      if (this.pulse !== null) clearInterval(this.pulse);
      this.pulse = null;
      this.peers.clear();
      if (!this.abort.signal.aborted) {
        this.setStatus('City reconnecting');
        this.scheduleRetry();
      }
    };
  }

  private message(event: MessageEvent<unknown>): void {
    let value: unknown;
    try {
      value = typeof event.data === 'string' ? (JSON.parse(event.data) as unknown) : null;
    } catch {
      this.socket?.close(1008);
      return;
    }
    if (isSnapshot(value)) this.snapshot(value);
    else if (isPong(value)) this.clock.pong(value.client_ms, value.server_epoch_ms);
    else if (isChatResult(value)) {
      this.chat.receive(value);
      if (value.status === 'sent' && this.generation && value.speaker.speech?.kind === 'say')
        this.speech.observe(this.generation, [value.speaker]);
    } else if (isSpeechDelivery(value)) {
      if (value.generation === this.generation && !this.mutes.muted.has(value.speaker.id))
        this.speech.observe(value.generation, [value.speaker]);
    } else if (isMuteResult(value)) {
      this.mutes.receive(value);
      if (value.status === 'applied' && value.muted) this.speech.silence(value.target);
    } else if (isEmoteResult(value)) {
      this.gestures.receive(value);
      if (value.status !== 'unavailable') this.acting = value.emote;
    } else this.socket?.close(1008);
  }

  private snapshot(value: Snapshot): void {
    if (
      value.world_version !== __CITY_ASSET_VERSION__ ||
      (this.generation !== null && this.generation !== value.generation)
    ) {
      this.socket?.close(1000);
      return;
    }
    this.generation = value.generation;
    if (this.muteGeneration !== value.generation) {
      this.mutes.reset();
      this.muteGeneration = value.generation;
    }
    this.clock.receive(value.server_epoch_ms, value.city_epoch_ms, value.animation_seconds);
    const own = value.players.find((player) => player.id === this.id);
    if (own && (!this.acting || value.server_epoch_ms >= this.acting.started_at_ms))
      this.acting = own.emote ?? null;
    this.lastSnapshot = performance.now();
    this.count = value.count;
    this.tick = value.tick;
    this.retries = 0;
    this.peers.replace(
      value.players,
      this.id ?? '',
      value.server_epoch_ms,
      this.clock.serverEpoch()
    );
    this.speech.observe(value.generation, value.players);
    this.socket?.send(JSON.stringify({ type: 'ack', tick: value.tick }));
    if (!this.received) {
      this.received = true;
      this.connected(
        this.clock,
        value.players.filter((player) => player.id !== this.id)
      );
    }
    this.setStatus(`Online · ${value.count} ${value.count === 1 ? 'visitor' : 'visitors'}`);
  }

  private sendPose(): void {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    const now = performance.now();
    if (now - this.lastSnapshot > 3000 || socket.bufferedAmount > 65536) {
      socket.close(1000);
      return;
    }
    const pose = this.pose();
    const key = JSON.stringify(pose);
    if (key !== this.lastPose) {
      this.sequence++;
      socket.send(JSON.stringify({ ...pose, type: 'pose', seq: this.sequence }));
      this.lastPose = key;
    }
    if (now - this.lastPing > 1000) {
      socket.send(JSON.stringify({ type: 'ping', client_ms: now }));
      this.lastPing = now;
    }
  }

  private scheduleRetry(): void {
    if (this.retry !== null) clearTimeout(this.retry);
    const delay = Math.min(10000, 1000 * 2 ** this.retries++);
    this.retry = setTimeout(() => {
      void this.connect();
    }, delay);
  }
}
